import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { naraEvents } from '../../../mocks/events';

interface ArtistEventsProps {
  artistId: number | null;
  accentColor: string;
}

export function ArtistEvents({ artistId, accentColor }: ArtistEventsProps) {
  const navigate = useNavigate();

  const events = useMemo(() => {
    if (artistId === null) return [];
    return naraEvents.filter((e) => e.artistIds.includes(artistId));
  }, [artistId]);

  if (events.length === 0) return null;

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    const year = d.getFullYear();
    const month = d.getMonth() + 1;
    const day = d.getDate();
    const weekdays = ['日', '月', '火', '水', '木', '金', '土'];
    const weekday = weekdays[d.getDay()];
    return `${year}.${String(month).padStart(2, '0')}.${String(day).padStart(2, '0')} (${weekday})`;
  };

  return (
    <div>
      {/* セクションヘッダー */}
      <div className="flex items-center gap-3 mb-6">
        <div
          className="w-2 h-2 rounded-full"
          style={{ background: accentColor, boxShadow: `0 0 10px ${accentColor}` }}
        />
        <p className="text-gray-500 text-xs tracking-widest uppercase">
          Events / {events.length} upcoming
        </p>
      </div>

      {/* グラスモーフィズムコンテナ */}
      <div
        className="rounded-2xl p-5 border backdrop-blur-md"
        style={{
          background: 'rgba(255,255,255,0.02)',
          borderColor: `${accentColor}18`,
          boxShadow: `0 0 40px ${accentColor}08`,
        }}
      >
        <div className="space-y-4">
          {events.map((event) => (
            <a
              key={event.id}
              href={`/event/${event.id}`}
              onClick={(e) => {
                e.preventDefault();
                navigate(`/event/${event.id}`);
              }}
              className="group flex gap-4 sm:gap-5 rounded-xl border overflow-hidden transition-all duration-500 cursor-pointer"
              style={{
                background: 'linear-gradient(135deg, rgba(26,26,26,0.8), rgba(15,15,15,0.9))',
                borderColor: `${accentColor}18`,
                boxShadow: `0 0 20px ${accentColor}06`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = `${accentColor}55`;
                e.currentTarget.style.boxShadow = `0 8px 30px ${accentColor}20, 0 0 0 1px ${accentColor}20`;
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = `${accentColor}18`;
                e.currentTarget.style.boxShadow = `0 0 20px ${accentColor}06`;
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              {/* Thumbnail */}
              <div className="relative w-28 h-28 sm:w-36 sm:h-32 flex-shrink-0 overflow-hidden">
                <img
                  src={event.thumbnail}
                  alt={event.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0f0f0f]/60" />
                <div
                  className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider backdrop-blur-sm"
                  style={{
                    background: `${event.color}25`,
                    color: event.color,
                    border: `1px solid ${event.color}40`,
                  }}
                >
                  {event.category}
                </div>
              </div>

              {/* Info */}
              <div className="flex-1 py-3 pr-4 flex flex-col justify-center min-w-0">
                <h4 className="text-white font-bold text-sm sm:text-base truncate group-hover:opacity-80 transition-opacity">
                  {event.title}
                </h4>

                <div className="flex items-center gap-2 mt-1.5">
                  <i className="ri-calendar-line text-xs" style={{ color: accentColor }} />
                  <span className="text-gray-400 text-xs">{formatDate(event.date)}</span>
                </div>

                <div className="flex items-center gap-2 mt-1">
                  <i className="ri-map-pin-line text-xs" style={{ color: accentColor }} />
                  <span className="text-gray-400 text-xs truncate">{event.venue}</span>
                </div>

                <div className="flex items-center justify-between mt-2">
                  <span
                    className="text-xs font-bold px-2 py-0.5 rounded-full"
                    style={{
                      color: accentColor,
                      background: `${accentColor}15`,
                      border: `1px solid ${accentColor}30`,
                    }}
                  >
                    {event.price}
                  </span>
                  <div
                    className="flex items-center gap-1 text-[10px] tracking-wider opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ color: accentColor }}
                  >
                    <span>DETAIL</span>
                    <i className="ri-arrow-right-s-line text-xs group-hover:translate-x-0.5 transition-transform" />
                  </div>
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
